import { common, createLowlight } from "lowlight";
import {
	CLI_SUBCOMMAND_PATTERN,
	CLI_TOOL_PATTERN,
	looksLikePrismaSchema,
	prismaGrammar,
} from "@/lib/highlight-languages";

const lowlight = createLowlight(common);
lowlight.register("prisma", prismaGrammar);

const CLI_TOOLS = new RegExp(`\\b(${CLI_TOOL_PATTERN})\\b`, "g");
const CLI_SUBCOMMANDS = new RegExp(`\\b(${CLI_SUBCOMMAND_PATTERN})\\b`, "g");
const CLI_FLAGS = /((?:^|\s)(-{1,2}[\w-]+))/g;
const CLI_STRINGS = /("(?:[^"\\]|\\.)*"|'(?:[^'\\]|\\.)*')/g;
const CLI_COMMENTS = /(#.*)$/gm;

function escapeHtml(str: string) {
	return str.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

function unescapeHtml(str: string) {
	return str
		.replace(/&amp;/g, "&")
		.replace(/&lt;/g, "<")
		.replace(/&gt;/g, ">")
		.replace(/&quot;/g, '"')
		.replace(/&#39;/g, "'");
}

function highlightCliText(text: string): string {
	const placeholders: string[] = [];

	const ph = (html: string) => {
		const id = `\x00${placeholders.length}\x00`;
		placeholders.push(html);
		return id;
	};

	let out = text;

	// Comments
	out = out.replace(CLI_COMMENTS, (m) => ph(`<span class="hljs-comment">${escapeHtml(m)}</span>`));

	// Strings
	out = out.replace(CLI_STRINGS, (m) => ph(`<span class="hljs-string">${escapeHtml(m)}</span>`));

	// CLI tools (built_in)
	out = out.replace(CLI_TOOLS, (m) => ph(`<span class="hljs-built_in">${m}</span>`));

	// Subcommands (keyword)
	out = out.replace(CLI_SUBCOMMANDS, (m) => ph(`<span class="hljs-keyword">${m}</span>`));

	// Flags (attr)
	out = out.replace(CLI_FLAGS, (_, prefix, flag) => {
		const space = prefix.startsWith(" ") ? " " : "";
		return space + ph(`<span class="hljs-attr">${flag}</span>`);
	});

	return out.replace(/\x00(\d+)\x00/g, (_, i) => placeholders[Number(i)]);
}

// Convert lowlight hast tree to HTML string
function hastToHtml(node: { type: string; value?: string; tagName?: string; properties?: Record<string, unknown>; children?: unknown[] }): string {
	if (node.type === "text") return escapeHtml(node.value ?? "");
	if (node.type === "element") {
		const cls = Array.isArray(node.properties?.className) ? (node.properties.className as string[]).join(" ") : "";
		const attrs = cls ? ` class="${cls}"` : "";
		const children = (node.children ?? []).map((c) => hastToHtml(c as Parameters<typeof hastToHtml>[0])).join("");
		return `<span${attrs}>${children}</span>`;
	}
	if (node.type === "root") {
		return (node.children ?? []).map((c) => hastToHtml(c as Parameters<typeof hastToHtml>[0])).join("");
	}
	return "";
}

const CLI_LANGS = new Set(["shell", "bash", "sh", "zsh", "plaintext", "text"]);

// Apply CLI or lowlight highlighting to all <pre><code> blocks in an HTML string
export function highlightCliInHtml(html: string): string {
	return html.replace(
		/(<pre[^>]*>)<code([^>]*)>([\s\S]*?)<\/code>(<\/pre>)/g,
		(_, preOpen, codeAttrs, codeContent, preClose) => {
			// Skip if already highlighted
			if (codeContent.includes("hljs-")) return _;

			// Extract language from class="language-xxx"
			const langMatch = (codeAttrs as string).match(/class="[^"]*language-([^"\s]+)/);
			const declaredLang = langMatch?.[1] ?? "shell";
			const plainText = unescapeHtml(codeContent);
			const lang = looksLikePrismaSchema(plainText) ? "prisma" : declaredLang;

			if (CLI_LANGS.has(lang)) {
				const highlighted = highlightCliText(codeContent);
				return `${preOpen}<code${codeAttrs}>${highlighted}</code>${preClose}`;
			}

			// Use lowlight for TypeScript, JavaScript, etc.
			try {
				const supported = lowlight.listLanguages().includes(lang);
				const tree = supported ? lowlight.highlight(lang, plainText) : lowlight.highlightAuto(plainText);
				const highlighted = hastToHtml(tree as Parameters<typeof hastToHtml>[0]);
				return `${preOpen}<code${codeAttrs}>${highlighted}</code>${preClose}`;
			} catch {
				return _;
			}
		},
	);
}
